/**
 * Cookie Consent Banner
 * 
 * Displays cookie consent banner for web platform to comply with GDPR/ePrivacy Directive
 * Only shown on web platform
 */

import { useState, useEffect } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { ThemedText, Body } from '@/components/ui/Text';
import { useUserStore } from '@/state/userStore';
import { grantConsent, hasConsent } from '@/services/consentManagement';
import { getCurrentPrivacyPolicyVersion } from '@/services/consentManagement';

const COOKIE_CONSENT_STORAGE_KEY = '@tripflow_cookie_consent';

type CookieConsentState = 'pending' | 'granted' | 'denied';

export function CookieConsentBanner() {
  const theme = useTheme();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only show on web platform
    if (Platform.OS !== 'web') {
      return;
    }

    checkConsentStatus();
  }, [firebaseUser?.uid]);

  const readLocalConsentState = (): CookieConsentState => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'pending';
    }
    const stored = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (stored === 'granted' || stored === 'denied') {
      return stored;
    }
    return 'pending';
  };

  const resolveEffectiveConsent = async (): Promise<CookieConsentState> => {
    if (!firebaseUser?.uid) {
      return readLocalConsentState();
    }
    const hasAnalyticsConsent = await hasConsent(firebaseUser.uid, 'analytics');
    if (hasAnalyticsConsent) {
      return 'granted';
    }
    const local = readLocalConsentState();
    return local === 'denied' ? 'denied' : 'pending';
  };

  const checkConsentStatus = async () => {
    try {
      const consentState = await resolveEffectiveConsent();
      setShowBanner(consentState === 'pending');
    } catch (error) {
      console.error('[CookieConsent] Error checking consent:', error);
      // Show banner on error to be safe
      setShowBanner(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      // Store in localStorage for web
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'granted');
      }

      // Store in Firestore if user is logged in
      if (firebaseUser?.uid) {
        const version = getCurrentPrivacyPolicyVersion();
        await grantConsent(firebaseUser.uid, 'analytics', version);
      }

      setShowBanner(false);
      
      // Initialize analytics/tracking here if needed
      // This is where you would initialize Firebase Analytics, etc.
    } catch (error) {
      console.error('[CookieConsent] Error accepting consent:', error);
    }
  };

  const handleDeny = () => {
    // Store in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'denied');
    }

    // Note: We don't store "denied" in Firestore to avoid tracking
    // The absence of consent is sufficient

    setShowBanner(false);
    
    // Don't initialize analytics/tracking
  };

  const handleCustomize = () => {
    // Navigate to consent management screen
    // For web, we could open a modal or navigate
    // For now, just accept all (user can manage later in settings)
    handleAccept();
  };

  if (Platform.OS !== 'web' || loading || !showBanner) {
    return null;
  }

  return (
    <View
      style={{
        position: 'fixed' as unknown as 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: theme.colors.surface,
        borderTopWidth: 1,
        borderTopColor: theme.colors.borderMuted,
        padding: theme.spacing(2),
        zIndex: 10000,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 5,
      }}
    >
      <View
        style={{
          maxWidth: 1200,
          marginHorizontal: 'auto',
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(2),
          flexWrap: 'wrap',
        }}
      >
        <View style={{ flex: 1, minWidth: 300, gap: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <Ionicons name="information-circle" size={20} color={theme.colors.primary} />
            <ThemedText variant="body" style={{ fontWeight: '600' }}>
              Cookie Consent
            </ThemedText>
          </View>
          <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
            We use cookies and similar technologies to analyze usage, improve our services, and personalize your experience. 
            By clicking "Accept All", you consent to our use of cookies. You can manage your preferences in Settings at any time.
          </Body>
        </View>

        <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), flexWrap: 'wrap' }}>
          <Button
            label="Accept All"
            variant="primary"
            onPress={handleAccept}
            style={{ minWidth: 100 }}
          />
          <Button
            label="Deny"
            variant="ghost"
            onPress={handleDeny}
            style={{ minWidth: 80 }}
          />
        </View>
      </View>
    </View>
  );
}

